import React, { useState, useRef, useEffect } from 'react';
import { 
  X, 
  Clock, 
  CheckCircle, 
  ThumbsUp, 
  RefreshCw,
  MessageSquare,
  User,
  Calendar,
  FileText,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import './BrandFeedbackModal.css';
import VideoSelectionModal from './VideoSelectionModal';
import RevisionRequestModal from './RevisionRequestModal';
import SubmissionService from '../services/submissionService';
import SubmissionVideoPlayer from './SubmissionVideoPlayer';
import { formatVideoTime, parseVideoTimestamp } from '../utils/videoPlayerUtils';
import toast from 'react-hot-toast';

const RevisionSubmissionModal = ({ 
  isOpen, 
  onClose, 
  submission, 
  onApprove,
  onRequestRevision
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [activeVideoTab, setActiveVideoTab] = useState(0);
  const [showVideoSelection, setShowVideoSelection] = useState(false);
  const [showRevisionRequest, setShowRevisionRequest] = useState(false);
  const [selectedVideosForRevision, setSelectedVideosForRevision] = useState([]);
  const [showOriginalFeedback, setShowOriginalFeedback] = useState(false);
  const [showSafeZone, setShowSafeZone] = useState(false);
  
  const playerRef = useRef(null);

  // Initialize modal state
  useEffect(() => {
    if (isOpen && submission?.content) {
      setShowApprovalConfirm(false);
      setShowOriginalFeedback(false);
      
      // Find first item that needs revision, or default to first video
      const itemsNeedingRevision = submission.content.filter(video => 
        video.status === 'needs_revision' || 
        (video.feedback && (video.feedback.generalNotes || (Array.isArray(video.feedback.timestampedFeedback) && video.feedback.timestampedFeedback.length > 0)))
      );
      
      if (itemsNeedingRevision.length > 0) {
        // Set to first item needing revision
        const firstItemIndex = submission.content.findIndex(v => v.id === itemsNeedingRevision[0].id);
        if (firstItemIndex >= 0) {
          setActiveVideoTab(firstItemIndex);
        } else {
          setActiveVideoTab(0);
        }
      } else {
        // No items need revision, default to first main video
        const firstMainVideoIndex = submission.content.findIndex(v => !v.variationType);
        setActiveVideoTab(firstMainVideoIndex >= 0 ? firstMainVideoIndex : 0);
      }
    }
  }, [isOpen, submission]);

  const seekToTimestamp = (timestamp) => {
    const timeInSeconds = parseVideoTimestamp(timestamp);
    playerRef.current?.seekTo(timeInSeconds);
  };

  const handleApprove = () => {
    setShowApprovalConfirm(true);
  };

  const confirmApproval = async () => {
    setIsSubmitting(true);
    try {
      const originalData = submission._originalData;
      
      if (!originalData) {
        throw new Error('Submission data not found');
      }

      // Approve all videos in the submission
      await SubmissionService.approveAllVideos(
        originalData.campaignId,
        originalData.briefId,
        submission.id
      );

      toast.success('Content approved successfully!');
      
      if (onApprove) onApprove(submission.id);
      onClose();
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('Failed to approve content: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestRevision = () => {
    // If only one video, go straight to revision modal
    if (submission.content.length === 1) {
      setSelectedVideosForRevision([submission.content[0]]);
      setShowRevisionRequest(true);
    } else {
      // Multiple videos - show selection modal
      setShowVideoSelection(true);
    }
  };

  const handleVideoSelectionConfirm = (selectedVideos) => {
    setSelectedVideosForRevision(selectedVideos);
    setShowVideoSelection(false);
    setShowRevisionRequest(true);
  };

  const handleRevisionSubmit = async (revisionRequest) => {
    // Don't submit immediately - show confirmation modal first
    // Close the revision request modal but keep the submission modal open
    setShowRevisionRequest(false);
    
    // Pass the revision request data to parent to show confirmation modal
    if (onRequestRevision) {
      onRequestRevision(submission, revisionRequest);
    }
    
    // Don't call onClose() - keep the revision submission modal open
    // User will see confirmation modal on top
  };

  if (!isOpen || !submission) return null;

  // Get the current video's URL
  const getCurrentVideoUrl = () => {
    const video = submission.content[activeVideoTab];
    return video?.downloadURL || video?.videoUrl;
  };

  const currentVideo = submission.content[activeVideoTab];

  return (
    <div className="brand-feedback-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="brand-feedback-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="brand-feedback-modal-header">
          <div className="brand-feedback-modal-title-section">
            <div className="brand-feedback-creator-info">
              <div className="brand-feedback-creator-avatar">
                <img 
                  src={submission.creator.avatar} 
                  alt={submission.creator.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="brand-feedback-avatar-fallback">
                  {submission.creator.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
              </div>
              <div className="brand-feedback-creator-details">
                <h3 className="brand-feedback-modal-title">
                  {submission.revisionCount === 0 
                    ? 'Review Content Submission' 
                    : `Review Content Submission - Revision ${submission.revisionCount}`}
                </h3>
                <p className="brand-feedback-modal-subtitle">
                  {submission.creator.name} - {submission.campaign}
                </p>
              </div>
            </div>
          </div>
          <button className="brand-feedback-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="brand-feedback-modal-content">
          <div className="brand-feedback-review-section">
            {/* Video Preview */}
            <div className="brand-feedback-video-section">
              {/* Revised Items - Show as tabs (SaaS style) when multiple, or single item */}
              {(() => {
                // Find all items that need revision
                const revisedItems = submission.content.filter(video => 
                  video.status === 'needs_revision' || 
                  (video.feedback && (video.feedback.generalNotes || (Array.isArray(video.feedback.timestampedFeedback) && video.feedback.timestampedFeedback.length > 0)))
                );
                
                // If there are revised items, show them as tabs
                if (revisedItems.length > 0) {
                  // If multiple items, show tabs; if single, just show the title
                  if (revisedItems.length > 1) {
                    return (
                      <div className="brand-feedback-revision-tabs-container">
                        <div className="brand-feedback-revision-tabs">
                          {revisedItems.map((video) => {
                            const originalIndex = submission.content.findIndex(v => v.id === video.id);
                            const isActive = activeVideoTab === originalIndex;
                            const isVariation = video.variationType || video.type === 'hook' || video.type === 'cta';
                            
                            return (
                              <button
                                key={video.id}
                                className={`brand-feedback-revision-tab ${isActive ? 'brand-feedback-revision-tab-active' : ''} ${video.status === 'needs_revision' ? 'brand-feedback-revision-tab-needs-revision' : ''}`}
                                onClick={() => setActiveVideoTab(originalIndex)}
                              >
                                <span className="brand-feedback-revision-tab-title">
                                  {video.title}
                                  {isVariation && (
                                    <span className="brand-feedback-revision-tab-type">
                                      ({video.variationType?.toUpperCase() || video.type?.toUpperCase()})
                                    </span>
                                  )}
                                </span>
                                {video.status === 'needs_revision' && (
                                  <span className="brand-feedback-revision-tab-indicator"></span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        <div className="brand-feedback-revision-tabs-indicator" style={{
                          left: revisedItems.findIndex((video) => {
                            const originalIndex = submission.content.findIndex(v => v.id === video.id);
                            return activeVideoTab === originalIndex;
                          }) * (100 / revisedItems.length) + '%',
                          width: (100 / revisedItems.length) + '%'
                        }}></div>
                      </div>
                    );
                  } else {
                    // Single item - just show title
                    return (
                      <div className="brand-feedback-revision-single-item">
                        <h4 className="brand-feedback-revision-single-title">Revised Items</h4>
                      </div>
                    );
                  }
                }
                
                // If no items need revision, show dropdown for all videos (fallback)
                // Filter to only main videos (no variations) for the dropdown
                const mainVideos = submission.content.filter(video => !video.variationType);
                // Find the current video index in the filtered list
                const currentMainVideoIndex = mainVideos.findIndex(v => submission.content[activeVideoTab]?.id === v.id);
                const safeActiveIndex = currentMainVideoIndex >= 0 ? currentMainVideoIndex : 0;
                
                return (
                  <div className="brand-feedback-selectors-row">
                    <div className="brand-feedback-video-selector">
                      <label className="brand-feedback-select-label">Video</label>
                      <select 
                        value={safeActiveIndex} 
                        onChange={(e) => {
                          const selectedIndex = parseInt(e.target.value);
                          const selectedVideo = mainVideos[selectedIndex];
                          if (selectedVideo) {
                            const originalIndex = submission.content.findIndex(v => v.id === selectedVideo.id);
                            if (originalIndex >= 0) {
                              setActiveVideoTab(originalIndex);
                            }
                          }
                        }}
                        className="brand-feedback-video-dropdown"
                      >
                        {mainVideos.map((video, index) => {
                          return (
                            <option key={video.id} value={index}>
                              {video.title}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                );
              })()}
              
              <SubmissionVideoPlayer
                ref={playerRef}
                video={submission.content[activeVideoTab]}
                videoUrl={getCurrentVideoUrl()}
                showSafeZone={showSafeZone}
                badge={(() => {
                  const vid = submission.content[activeVideoTab];
                  const isLatestRevision = vid?.revisionCount === submission.revisionCount;
                  return isLatestRevision ? (
                    <div className="brand-feedback-revision-indicator">
                      <CheckCircle size={16} />
                      <span>Latest Revision</span>
                    </div>
                  ) : null;
                })()}
              />
              <div className="brand-feedback-video-controls-row">
                <label className="brand-feedback-safe-zone-toggle-switch">
                  <input
                    type="checkbox"
                    checked={showSafeZone}
                    onChange={(e) => setShowSafeZone(e.target.checked)}
                  />
                  <span className="brand-feedback-toggle-slider"></span>
                  <span className="brand-feedback-toggle-label">Show Safe Zone</span>
                </label>
              </div>
            </div>

            {/* Video-Specific Notes */}
            <div className="brand-feedback-notes-section">
              <h4 className="brand-feedback-section-title">Creator Notes - {submission.content[activeVideoTab].title}</h4>
              
              {/* Show Original Feedback Toggle */}
              {currentVideo?.feedback && (
                <div className="brand-feedback-original-feedback-toggle">
                  <button 
                    onClick={() => setShowOriginalFeedback(!showOriginalFeedback)}
                    className="brand-feedback-toggle-btn"
                  >
                    {showOriginalFeedback ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {showOriginalFeedback ? 'Hide' : 'Show'} Original Feedback
                  </button>
                </div>
              )}

              {/* Original Feedback (Collapsible) */}
              {showOriginalFeedback && currentVideo?.feedback && (
                <div className="brand-feedback-original-feedback-section">
                  <h5>Your Original Feedback:</h5>
                  {currentVideo.feedback.generalNotes && (
                    <div className="brand-feedback-creator-note">
                      <div className="brand-feedback-note-header">
                        <MessageSquare size={16} />
                        <span>General Feedback</span>
                      </div>
                      <p className="brand-feedback-note-content">{currentVideo.feedback.generalNotes}</p>
                    </div>
                  )}
                  {currentVideo.feedback.timestampedFeedback && currentVideo.feedback.timestampedFeedback.length > 0 && (
                    <div className="brand-feedback-timestamped-notes">
                      <div className="brand-feedback-note-header">
                        <Clock size={16} />
                        <span>Timestamped Feedback</span>
                      </div>
                      <div className="brand-feedback-timestamp-list">
                        {currentVideo.feedback.timestampedFeedback.map((feedback, index) => {
                          // Handle different timestamp formats
                          let displayTime = '';
                          if (typeof feedback.time === 'number') {
                            displayTime = formatVideoTime(feedback.time);
                          } else if (typeof feedback.time === 'string') {
                            // If it's already formatted as a string, use it directly
                            displayTime = feedback.time;
                          } else if (feedback.timestamp) {
                            // Check if there's a timestamp field instead
                            displayTime = typeof feedback.timestamp === 'number' 
                              ? formatVideoTime(feedback.timestamp) 
                              : feedback.timestamp;
                          } else {
                            displayTime = '0:00';
                          }
                          
                          return (
                            <div 
                              key={index} 
                              className="brand-feedback-timestamp-item"
                              onClick={() => seekToTimestamp(feedback.timestamp || feedback.time)}
                              style={{ cursor: 'pointer' }}
                              title="Click to seek to this timestamp"
                            >
                              <div className="brand-feedback-timestamp-time">
                                {displayTime}
                              </div>
                              <div className="brand-feedback-timestamp-content">{feedback.note}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {submission.content[activeVideoTab].notes && (
                <div className="brand-feedback-creator-note">
                  <div className="brand-feedback-note-header">
                    <FileText size={16} />
                    <span>Video Notes{submission.revisionCount === 0 ? '' : ` (Revision ${submission.revisionCount})`}</span>
                  </div>
                  <p className="brand-feedback-note-content">{submission.content[activeVideoTab].notes}</p>
                </div>
              )}

              {submission.content[activeVideoTab].timestampedNotes && submission.content[activeVideoTab].timestampedNotes.length > 0 && (
                <div className="brand-feedback-timestamped-notes">
                  <div className="brand-feedback-note-header">
                    <Clock size={16} />
                    <span>Timestamped Notes{submission.revisionCount === 0 ? '' : ` (Revision ${submission.revisionCount})`}</span>
                  </div>
                  <div className="brand-feedback-timestamp-list">
                    {submission.content[activeVideoTab].timestampedNotes.map((note, index) => (
                      <div 
                        key={index} 
                        className="brand-feedback-timestamp-item"
                        onClick={() => seekToTimestamp(note.timestamp || note.time)}
                        style={{ cursor: 'pointer' }}
                        title="Click to seek to this timestamp"
                      >
                        <div className="brand-feedback-timestamp-time">
                          {note.timestamp || (note.time ? formatVideoTime(note.time) : '0:00')}
                        </div>
                        <div className="brand-feedback-timestamp-content">{note.note}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submission Details */}
              <div className="brand-feedback-submission-details">
                <div className="brand-feedback-detail-item">
                  <User size={16} />
                  <span><strong>Creator:</strong> {submission.creator.name} ({submission.creator.handle})</span>
                </div>
                <div className="brand-feedback-detail-item">
                  <Calendar size={16} />
                  <span><strong>Submitted:</strong> {new Date(submission.submittedAt).toLocaleDateString()}</span>
                </div>
                <div className="brand-feedback-detail-item">
                  <MessageSquare size={16} />
                  <span><strong>Campaign:</strong> {submission.campaign}</span>
                </div>
                <div className="brand-feedback-detail-item">
                  <RefreshCw size={16} />
                  <span><strong>Revisions:</strong> {submission.revisionCount || 0}/2</span>
                </div>
              </div>

              {/* Revision History */}
              {(submission.revisionCount > 0 || submission.status === 'revision_requested' || submission.status === 'needs_revision') && (
                <div className="brand-feedback-revision-history">
                  <h4>Revision History</h4>
                  <div className="revision-timeline">
                    <div className="revision-item revision-submitted">
                      <div className="revision-icon">
                        <CheckCircle size={16} />
                      </div>
                      <div className="revision-content">
                        <div className="revision-title">Initial Submission</div>
                        <div className="revision-date">{new Date(submission.submittedAt).toLocaleDateString()}</div>
                        <div className="revision-status status-submitted">Submitted</div>
                      </div>
                    </div>

                    {submission.revisionCount > 0 && (
                      <>
                        {Array.from({ length: submission.revisionCount }, (_, index) => {
                          const revisionNumber = index + 1; // Revision 1, Revision 2
                          const isFinalRevision = revisionNumber === 2;
                          return (
                          <div key={`revision-${revisionNumber}`} className="revision-item revision-requested">
                            <div className="revision-icon">
                              <RefreshCw size={16} />
                            </div>
                            <div className="revision-content">
                              <div className="revision-title">
                                {isFinalRevision ? 'Final Revision 2' : `Revision ${revisionNumber}`}
                              </div>
                              <div className="revision-date">
                                {submission.lastRevisionRequestedAt 
                                  ? new Date(submission.lastRevisionRequestedAt).toLocaleDateString()
                                  : 'Recently requested'
                                }
                              </div>
                              <div className="revision-status status-revision-requested">Revision Requested</div>
                            </div>
                          </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="brand-feedback-modal-footer">
          <div className="brand-feedback-modal-actions">
            <button 
              className="brand-feedback-cancel-btn"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            
            <div className="brand-feedback-action-buttons">
              <button 
                className="brand-feedback-approve-btn"
                onClick={handleApprove}
                disabled={isSubmitting}
              >
                <ThumbsUp size={16} />
                Approve
              </button>
              
              {/* Disable "Request Another Revision" if this is the final revision (2/2) */}
              {/* revisionCount 0 = Original submission, revisionCount 1 = Revision 1, revisionCount 2 = Revision 2 (final) */}
              <button 
                className="brand-feedback-revision-btn"
                onClick={handleRequestRevision}
                disabled={isSubmitting || (submission.revisionCount || 0) >= 2}
                title={(submission.revisionCount || 0) >= 2 ? "Final revision (2/2) - Cannot request another revision" : ""}
                style={(submission.revisionCount || 0) >= 2 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <RefreshCw size={16} />
                Request Another Revision
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Approval Confirmation Modal */}
      {showApprovalConfirm && (
        <div className="brand-feedback-confirm-overlay">
          <div className="brand-feedback-confirm-modal">
            <div className="brand-feedback-confirm-header">
              <h3>Confirm Approval</h3>
              <button onClick={() => setShowApprovalConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="brand-feedback-confirm-content">
              <p>Are you sure you want to approve this revision?</p>
              <p className="brand-feedback-confirm-subtitle">
                This action will mark the content as approved and notify the creator.
              </p>
            </div>
            <div className="brand-feedback-confirm-actions">
              <button 
                className="brand-feedback-confirm-cancel"
                onClick={() => setShowApprovalConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="brand-feedback-confirm-approve"
                onClick={confirmApproval}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Approving...' : 'Yes, Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Selection Modal */}
      <VideoSelectionModal
        isOpen={showVideoSelection}
        onClose={() => setShowVideoSelection(false)}
        submission={submission}
        onConfirm={handleVideoSelectionConfirm}
      />

      {/* Revision Request Modal */}
      <RevisionRequestModal
        isOpen={showRevisionRequest}
        onClose={() => setShowRevisionRequest(false)}
        submission={submission}
        selectedVideos={selectedVideosForRevision}
        onSubmit={handleRevisionSubmit}
      />
    </div>
  );
};

export default RevisionSubmissionModal;

